import { useState, useRef, useCallback, useEffect } from 'react'
import { useAccounts } from '@/api/hooks/useAccounts'
import { getAuthHeaders } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import {
  Trash2, Loader2, Search, Image, Video, Film, CheckSquare, Square,
  ArrowDownUp, AlertTriangle, ChevronDown, Facebook, Instagram,
  EyeOff, Eye, Copy,
} from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'

const V2 = '/api/v2'

interface Post {
  id: string
  message?: string
  imageUrl?: string
  createdAt: string
  type?: string
  platform?: string
}

interface DeleteProgress {
  current: number
  total: number
  deleted: number
  failed: number
  lastId?: string
  lastStatus?: string
}

function getMonthOptions(): { value: string; label: string }[] {
  return [
    { value: '01', label: 'Janeiro' },
    { value: '02', label: 'Fevereiro' },
    { value: '03', label: 'Marco' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Maio' },
    { value: '06', label: 'Junho' },
    { value: '07', label: 'Julho' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' },
  ]
}

function getYearOptions(): string[] {
  const now = new Date()
  const years: string[] = []
  for (let y = now.getFullYear(); y >= 2015; y--) {
    years.push(String(y))
  }
  return years
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}

function getPostType(post: Post): string {
  const t = post.type?.toLowerCase() || ''
  if (t === 'video' || t === 'VIDEO') return 'video'
  if (t === 'album' || t === 'CAROUSEL_ALBUM') return 'carrossel'
  if (t === 'photo' || t === 'IMAGE' || post.imageUrl) return 'foto'
  if (t === 'share') return 'share'
  return 'texto'
}

function getPostTypeBadge(postType: string) {
  switch (postType) {
    case 'video':
      return <Badge variant="secondary" className="text-[10px] gap-1"><Video className="h-3 w-3" />Video</Badge>
    case 'carrossel':
      return <Badge variant="secondary" className="text-[10px] gap-1"><Film className="h-3 w-3" />Carrossel</Badge>
    case 'foto':
      return <Badge variant="secondary" className="text-[10px] gap-1"><Image className="h-3 w-3" />Foto</Badge>
    default:
      return <Badge variant="outline" className="text-[10px]">Texto</Badge>
  }
}

function getThumbnail(post: Post): string | null {
  return post.imageUrl || null
}

export function Exclusao() {
  const { t } = useLanguage()
  const { data: accounts, isLoading: accountsLoading } = useAccounts()

  // Controls
  const [selectedAccount, setSelectedAccount] = useState('')
  const [platform, setPlatform] = useState<'fb' | 'ig'>('fb')

  // Date range — default last 3 months
  const now = new Date()
  const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1)
  const [sinceMonth, setSinceMonth] = useState(String(threeMonthsAgo.getMonth() + 1).padStart(2, '0'))
  const [sinceYear, setSinceYear] = useState(String(threeMonthsAgo.getFullYear()))
  const [untilMonth, setUntilMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'))
  const [untilYear, setUntilYear] = useState(String(now.getFullYear()))

  // Posts
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set())

  // Delete
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteProgress, setDeleteProgress] = useState<DeleteProgress | null>(null)

  // Hide/Clone
  const [actionInProgress, setActionInProgress] = useState<string | null>(null)
  const [actionProgress, setActionProgress] = useState<DeleteProgress | null>(null)
  const [cloneOpen, setCloneOpen] = useState(false)
  const [cloneTarget, setCloneTarget] = useState('')

  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // Auto-select first account
  useEffect(() => {
    if (accounts?.length && !selectedAccount) {
      setSelectedAccount(accounts[0].igUserId)
    }
  }, [accounts, selectedAccount])

  const loadPosts = useCallback(async (cursor?: string) => {
    if (!selectedAccount) {
      toast.error(t('Selecione uma conta'))
      return
    }

    setLoading(true)
    if (!cursor) {
      setPosts([])
      setSelected(new Set())
    }

    try {
      const since = `${sinceYear}-${sinceMonth}`
      const until = `${untilYear}-${untilMonth}`

      const params = new URLSearchParams({
        igUserId: selectedAccount,
        platform,
        since,
        until,
      })
      if (cursor) params.set('after', cursor)

      const res = await fetch(`${V2}/exclusao/posts?${params}`, {
        headers: { 'Accept': 'application/json', ...getAuthHeaders() },
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Erro ao carregar posts' }))
        throw new Error(err.error || `HTTP ${res.status}`)
      }

      const data = await res.json()
      const newPosts: Post[] = data.posts || []

      if (cursor) {
        setPosts(prev => [...prev, ...newPosts])
      } else {
        setPosts(newPosts)
      }

      setHasMore(!!data.paging?.next)
      setNextCursor(data.paging?.cursors?.after || null)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro desconhecido'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }, [selectedAccount, platform, sinceMonth, sinceYear, untilMonth, untilYear, t])

  const toggleSelect = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    setSelected(new Set(posts.map(p => p.id)))
  }, [posts])

  const invertSelection = useCallback(() => {
    setSelected(prev => {
      const next = new Set<string>()
      for (const p of posts) {
        if (!prev.has(p.id)) next.add(p.id)
      }
      return next
    })
  }, [posts])

  const handleDelete = useCallback(async () => {
    setConfirmOpen(false)
    if (selected.size === 0) return

    setDeleting(true)
    setDeleteProgress({ current: 0, total: selected.size, deleted: 0, failed: 0 })

    try {
      const res = await fetch(`${V2}/exclusao/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          igUserId: selectedAccount,
          postIds: Array.from(selected),
          platform,
        }),
      })

      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({ error: 'Erro ao deletar' }))
        throw new Error(err.error || `HTTP ${res.status}`)
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        if (!mountedRef.current) { reader.cancel(); break }
        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        let eventType = ''
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7)
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6))
              if (eventType === 'progress') {
                if (mountedRef.current) {
                  setDeleteProgress({
                    current: data.current,
                    total: data.total,
                    deleted: data.deleted,
                    failed: data.failed,
                    lastId: data.lastId,
                    lastStatus: data.lastStatus,
                  })
                }
              } else if (eventType === 'done') {
                if (mountedRef.current) {
                  setDeleteProgress({
                    current: data.total,
                    total: data.total,
                    deleted: data.deleted,
                    failed: data.failed,
                  })
                  toast.success(`${data.deleted} posts apagados com sucesso${data.failed > 0 ? ` (${data.failed} falharam)` : ''}`)
                }
              } else if (eventType === 'error') {
                toast.error(data.message || 'Erro durante exclusao')
              }
            } catch { /* ignore parse errors */ }
            eventType = ''
          }
        }
      }

      // Remove deleted posts from list
      if (mountedRef.current) {
        setPosts(prev => prev.filter(p => !selected.has(p.id)))
        setSelected(new Set())
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro desconhecido'
      toast.error(msg)
    } finally {
      if (mountedRef.current) {
        setDeleting(false)
        setDeleteProgress(null)
      }
    }
  }, [selected, selectedAccount, platform])

  const runSSEAction = useCallback(async (url: string, body: Record<string, unknown>, actionName: string) => {
    setActionInProgress(actionName)
    setActionProgress({ current: 0, total: selected.size, deleted: 0, failed: 0 })
    try {
      const res = await fetch(`${V2}${url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream', ...getAuthHeaders() },
        body: JSON.stringify(body),
      })
      if (!res.ok || !res.body) throw new Error('Request failed')
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        let evt = ''
        for (const line of lines) {
          if (line.startsWith('event: ')) evt = line.slice(7)
          else if (line.startsWith('data: ') && evt) {
            try {
              const d = JSON.parse(line.slice(6))
              if (evt === 'progress') setActionProgress(p => p ? { ...p, current: d.current, deleted: d.success ?? d.cloned ?? d.current, failed: d.failed ?? 0 } : p)
              if (evt === 'done') {
                const ok = d.success ?? d.cloned ?? 0
                const fail = d.failed ?? 0
                toast.success(`${actionName}: ${ok} ok${fail > 0 ? `, ${fail} falharam` : ''}`)
              }
            } catch {}
            evt = ''
          }
        }
      }
    } catch (e: any) {
      toast.error(e.message || 'Erro')
    } finally {
      setActionInProgress(null)
      setActionProgress(null)
    }
  }, [selected])

  const handleHide = useCallback(async (hide: boolean) => {
    if (selected.size === 0) return
    await runSSEAction('/exclusao/hide', {
      igUserId: selectedAccount,
      postIds: Array.from(selected),
      hide,
    }, hide ? 'Ocultar' : 'Desocultar')
    setSelected(new Set())
  }, [selected, selectedAccount, runSSEAction])

  const handleClone = useCallback(async () => {
    if (selected.size === 0 || !cloneTarget) return
    setCloneOpen(false)
    await runSSEAction('/exclusao/clone', {
      sourceIgUserId: selectedAccount,
      targetIgUserId: cloneTarget,
      postIds: Array.from(selected),
      platform,
    }, 'Clonar')
    setSelected(new Set())
  }, [selected, selectedAccount, cloneTarget, platform, runSSEAction])

  const monthOptions = getMonthOptions()
  const yearOptions = getYearOptions()

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold tracking-tight">{t('Exclusao de Conteudo')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('Apague posts publicados do Facebook e Instagram')}
        </p>
      </div>

      {/* Controls */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-end gap-3">
          {/* Account */}
          <div className="min-w-[200px] flex-1">
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{t('Conta')}</label>
            <Select value={selectedAccount} onValueChange={setSelectedAccount}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={accountsLoading ? t('Carregando...') : t('Selecione uma conta')} />
              </SelectTrigger>
              <SelectContent>
                {accounts?.map(acc => (
                  <SelectItem key={acc.igUserId} value={acc.igUserId}>
                    {acc.name || acc.username || acc.igUserId}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Platform Toggle */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{t('Plataforma')}</label>
            <div className="flex rounded-lg border border-border/60 bg-background">
              <button
                onClick={() => setPlatform('fb')}
                className={`flex items-center gap-1.5 rounded-l-lg px-3 py-2 text-sm font-medium transition-colors ${
                  platform === 'fb'
                    ? 'bg-blue-600 text-white'
                    : 'text-muted-foreground hover:bg-secondary'
                }`}
              >
                <Facebook className="h-4 w-4" />
                FB
              </button>
              <button
                onClick={() => setPlatform('ig')}
                className={`flex items-center gap-1.5 rounded-r-lg px-3 py-2 text-sm font-medium transition-colors ${
                  platform === 'ig'
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                    : 'text-muted-foreground hover:bg-secondary'
                }`}
              >
                <Instagram className="h-4 w-4" />
                IG
              </button>
            </div>
          </div>

          {/* Since */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{t('De')}</label>
            <div className="flex gap-1.5">
              <Select value={sinceMonth} onValueChange={setSinceMonth}>
                <SelectTrigger className="w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sinceYear} onValueChange={setSinceYear}>
                <SelectTrigger className="w-[80px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Until */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{t('Ate')}</label>
            <div className="flex gap-1.5">
              <Select value={untilMonth} onValueChange={setUntilMonth}>
                <SelectTrigger className="w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={untilYear} onValueChange={setUntilYear}>
                <SelectTrigger className="w-[80px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Load Button */}
          <Button onClick={() => loadPosts()} disabled={loading || !selectedAccount}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
            {t('Carregar posts')}
          </Button>
        </div>
      </div>

      {/* Delete Progress */}
      {deleting && deleteProgress && (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-destructive" />
              <h3 className="text-lg font-semibold">{t('Apagando posts...')}</h3>
            </div>

            {/* Progress bar */}
            <div className="h-3 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-destructive transition-all duration-300"
                style={{ width: `${deleteProgress.total > 0 ? (deleteProgress.current / deleteProgress.total) * 100 : 0}%` }}
              />
            </div>

            {/* Counter */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {deleteProgress.current} {t('de')} {deleteProgress.total} {t('processados')}
              </span>
              <div className="flex gap-4">
                <span className="text-green-500">{deleteProgress.deleted} {t('apagados')}</span>
                {deleteProgress.failed > 0 && (
                  <span className="text-destructive">{deleteProgress.failed} {t('falharam')}</span>
                )}
              </div>
            </div>

            {/* Last status */}
            {deleteProgress.lastId && (
              <p className="text-xs text-muted-foreground">
                {t('Ultimo')}: {deleteProgress.lastId} - {deleteProgress.lastStatus === 'deleted' ? t('apagado') : t('falhou')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Results */}
      {!deleting && posts.length > 0 && (
        <div className="space-y-4">
          {/* Stats & Selection Controls */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">
              {posts.length} {t('posts encontrados')}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={selectAll}>
                <CheckSquare className="mr-1.5 h-3.5 w-3.5" />
                {t('Selecionar todos')}
              </Button>
              <Button variant="outline" size="sm" onClick={invertSelection}>
                <ArrowDownUp className="mr-1.5 h-3.5 w-3.5" />
                {t('Inverter selecao')}
              </Button>
            </div>
          </div>

          {/* Post Grid */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {posts.map(post => {
              const thumb = getThumbnail(post)
              const postType = getPostType(post)
              const isSelected = selected.has(post.id)

              return (
                <div
                  key={post.id}
                  onClick={() => toggleSelect(post.id)}
                  className={`group relative cursor-pointer overflow-hidden rounded-xl border transition-all ${
                    isSelected
                      ? 'border-destructive/60 bg-destructive/5 ring-1 ring-destructive/30'
                      : 'border-border bg-card hover:border-border/80'
                  }`}
                >
                  {/* Checkbox */}
                  <div className="absolute left-2 top-2 z-10">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelect(post.id)}
                      className="h-5 w-5 border-2 bg-background/80 backdrop-blur-sm"
                    />
                  </div>

                  {/* Type Badge */}
                  <div className="absolute right-2 top-2 z-10">
                    {getPostTypeBadge(postType)}
                  </div>

                  {/* Thumbnail */}
                  <div className="aspect-square w-full overflow-hidden bg-secondary/50">
                    {thumb ? (
                      <img
                        src={thumb}
                        alt=""
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => {
                          ;(e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-muted-foreground/40">
                        {postType === 'video' ? (
                          <Video className="h-12 w-12" />
                        ) : (
                          <Image className="h-12 w-12" />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Post Info */}
                  <div className="p-2.5">
                    {post.message ? (
                      <p className="line-clamp-2 text-xs text-foreground/80">
                        {post.message}
                      </p>
                    ) : (
                      <p className="text-xs italic text-muted-foreground">{t('Sem texto')}</p>
                    )}
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      {formatDate(post.createdAt)}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Load more */}
          {hasMore && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => loadPosts(nextCursor || undefined)}
                disabled={loading}
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ChevronDown className="mr-2 h-4 w-4" />}
                {t('Carregar mais')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!deleting && !loading && posts.length === 0 && selectedAccount && (
        <div className="rounded-xl border border-border bg-card p-12 text-center">
          <Trash2 className="mx-auto h-12 w-12 text-muted-foreground/30" />
          <p className="mt-4 text-sm text-muted-foreground">
            {t('Selecione uma conta, periodo e clique em "Carregar posts" para comecar')}
          </p>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && posts.length === 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="aspect-square w-full animate-pulse bg-secondary/50" />
              <div className="space-y-2 p-2.5">
                <div className="h-3 w-3/4 animate-pulse rounded bg-secondary/50" />
                <div className="h-2 w-1/3 animate-pulse rounded bg-secondary/50" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sticky Action Bar */}
      {!deleting && selected.size > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border/60 bg-background/95 px-4 py-3 backdrop-blur-md md:left-64">
          <div className="mx-auto flex max-w-7xl items-center justify-between">
            <span className="text-sm font-medium">
              {selected.size} {t('post(s) selecionado(s)')}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setSelected(new Set())}>
                {t('Limpar')}
              </Button>
              {platform === 'fb' && (
                <>
                  <Button variant="outline" size="sm" onClick={() => handleHide(true)} disabled={!!actionInProgress}>
                    <EyeOff className="mr-1.5 h-3.5 w-3.5" /> {t('Ocultar')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleHide(false)} disabled={!!actionInProgress}>
                    <Eye className="mr-1.5 h-3.5 w-3.5" /> {t('Desocultar')}
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" onClick={() => setCloneOpen(true)} disabled={!!actionInProgress}>
                <Copy className="mr-1.5 h-3.5 w-3.5" /> {t('Clonar')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setConfirmOpen(true)}
                disabled={!!actionInProgress}
              >
                <Trash2 className="mr-1.5 h-4 w-4" />
                {t('Apagar')} {selected.size}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Action Progress (hide/clone) */}
      {actionInProgress && actionProgress && (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-3 mb-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <h3 className="text-lg font-semibold">{actionInProgress}...</h3>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: `${actionProgress.total > 0 ? (actionProgress.current / actionProgress.total) * 100 : 0}%` }} />
          </div>
          <p className="mt-2 text-sm text-muted-foreground">{actionProgress.current} / {actionProgress.total}</p>
        </div>
      )}

      {/* Clone Dialog */}
      <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-primary" />
              {t('Clonar posts para outra pagina')}
            </DialogTitle>
            <DialogDescription>
              {t('Selecione a pagina de destino para clonar')} {selected.size} {t('post(s).')}
              {' '}{t('O conteudo (texto + midia) sera republicado na pagina escolhida.')}
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{t('Pagina de destino')}</label>
            <Select value={cloneTarget} onValueChange={setCloneTarget}>
              <SelectTrigger>
                <SelectValue placeholder={t('Selecione...')} />
              </SelectTrigger>
              <SelectContent>
                {accounts?.filter(a => a.igUserId !== selectedAccount).map(acc => (
                  <SelectItem key={acc.igUserId} value={acc.igUserId}>
                    {acc.name || acc.username || acc.igUserId}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneOpen(false)}>{t('Cancelar')}</Button>
            <Button onClick={handleClone} disabled={!cloneTarget}>
              <Copy className="mr-2 h-4 w-4" />
              {t('Clonar')} {selected.size} {t('posts')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {t('Confirmar exclusao')}
            </DialogTitle>
            <DialogDescription>
              {t('Voce esta prestes a apagar')} <strong>{selected.size}</strong> {t('post(s) do')}{' '}
              {platform === 'fb' ? 'Facebook' : 'Instagram'}.{' '}
              {t('Esta acao e irreversivel. Os posts serao removidos permanentemente da plataforma.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              {t('Cancelar')}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t('Sim, apagar')} {selected.size} {t('posts')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
